// ====================================================================
// EXTENDED TIMER STORE - Quick Start Feature
// Add these methods to your existing timerStore.ts
// ====================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface TimerState {
  // ... existing state (keep as is)
  isRunning: boolean;
  isPaused: boolean;
  startTime: number | null;
  lastPauseTime: number | null;
  totalBreakDuration: number;
  projectId: string | null;
  activityId: string | null;
  description: string;

  // ... existing methods (keep as is)
  startTimer: (projectId: string, activityId: string, description?: string) => void;
  stopTimer: () => { elapsedSeconds: number; totalBreakDuration: number };
  pauseTimer: () => void;
  resumeTimer: () => void;
  resetTimer: () => void;
  getElapsedSeconds: () => number;
  getNetWorkingSeconds: () => number;

  // 🆕 NEW: Quick Start methods
  stopAndSaveDraft: () => Promise<void>;
  quickStartProject: (projectId: string, activityId: string, description?: string) => Promise<void>;
}

export const useTimerStore = create<TimerState>()(
  persist(
    (set, get) => ({
      // ... existing state values (keep as is)
      isRunning: false,
      isPaused: false,
      startTime: null,
      lastPauseTime: null,
      totalBreakDuration: 0,
      projectId: null,
      activityId: null,
      description: '',

      // ... existing methods (keep as is)
      startTimer: (projectId: string, activityId: string, description = '') => {
        set({
          isRunning: true,
          isPaused: false,
          startTime: Date.now(),
          lastPauseTime: null,
          totalBreakDuration: 0,
          projectId,
          activityId,
          description,
        });
      },

      pauseTimer: () => {
        const state = get();
        if (!state.isRunning || state.isPaused) return;
        set({
          isPaused: true,
          lastPauseTime: Date.now(),
        });
      },

      resumeTimer: () => {
        const state = get();
        if (!state.isPaused || !state.lastPauseTime) return;
        const pauseDuration = Math.floor((Date.now() - state.lastPauseTime) / 1000);
        set({
          isPaused: false,
          lastPauseTime: null,
          totalBreakDuration: state.totalBreakDuration + pauseDuration,
        });
      },

      stopTimer: () => {
        const state = get();
        if (!state.startTime) {
          return { elapsedSeconds: 0, totalBreakDuration: 0 };
        }
        const totalElapsedSeconds = Math.floor((Date.now() - state.startTime) / 1000);
        let finalBreakDuration = state.totalBreakDuration;
        if (state.isPaused && state.lastPauseTime) {
          const currentPauseDuration = Math.floor((Date.now() - state.lastPauseTime) / 1000);
          finalBreakDuration += currentPauseDuration;
        }
        set({
          isRunning: false,
          isPaused: false,
          startTime: null,
          lastPauseTime: null,
          totalBreakDuration: 0,
        });
        return {
          elapsedSeconds: totalElapsedSeconds,
          totalBreakDuration: finalBreakDuration
        };
      },

      resetTimer: () => {
        set({
          isRunning: false,
          isPaused: false,
          startTime: null,
          lastPauseTime: null,
          totalBreakDuration: 0,
          projectId: null,
          activityId: null,
          description: '',
        });
      },

      getElapsedSeconds: () => {
        const state = get();
        if (!state.isRunning || !state.startTime) return 0;
        return Math.floor((Date.now() - state.startTime) / 1000);
      },

      getNetWorkingSeconds: () => {
        const state = get();
        if (!state.isRunning || !state.startTime) return 0;
        const totalElapsed = Math.floor((Date.now() - state.startTime) / 1000);
        let currentBreakDuration = state.totalBreakDuration;
        if (state.isPaused && state.lastPauseTime) {
          const ongoingPauseDuration = Math.floor((Date.now() - state.lastPauseTime) / 1000);
          currentBreakDuration += ongoingPauseDuration;
        }
        return totalElapsed - currentBreakDuration;
      },

      // ================================================================
      // 🆕 NEW METHOD: Stop timer and save as draft
      // ================================================================
      stopAndSaveDraft: async () => {
        const state = get();
        
        // If timer is not running, do nothing
        if (!state.isRunning || !state.startTime || !state.projectId || !state.activityId) {
          return;
        }

        try {
          // Get company_id
          const { data: companyData, error: companyError } = await supabase.rpc('get_current_user_company_id');
          if (companyError || !companyData) {
            console.error('No company found');
            return;
          }

          // Get user
          const { data: userData } = await supabase.auth.getUser();
          if (!userData?.user) {
            console.error('Not logged in');
            return;
          }

          // Get employee_id
          const { data: employeeData, error: employeeError } = await supabase
            .from('employees')
            .select('id')
            .eq('user_id', userData.user.id)
            .maybeSingle();

          if (employeeError || !employeeData) {
            console.error('No employee found');
            return;
          }

          // Calculate times
          const now = Date.now();
          const startTime = state.startTime;
          let totalBreakDuration = state.totalBreakDuration;
          
          // Include current pause in break duration
          if (state.isPaused && state.lastPauseTime) {
            totalBreakDuration += Math.floor((now - state.lastPauseTime) / 1000);
          }

          const breakDurationMinutes = Math.floor(totalBreakDuration / 60);
          
          const formatTime = (timestamp: number) => {
            const date = new Date(timestamp);
            return date.toLocaleTimeString('nl-NL', {
              hour12: false,
              hour: '2-digit',
              minute: '2-digit'
            });
          };

          const startTimeStr = formatTime(startTime);
          const endTimeStr = formatTime(now);
          const dateStr = new Date().toISOString().split('T')[0];

          // Create draft entry
          const timeEntryData = {
            date: dateStr,
            start_time: startTimeStr,
            end_time: endTimeStr,
            break_duration: breakDurationMinutes,
            project_id: state.projectId,
            employee_id: employeeData.id,
            company_id: companyData,
            activity_id: state.activityId,
            notes: state.description || null,
            cost_category_id: null,
            hourly_rate: null,
            status: 'draft', // 🎯 KEY: Save as draft for Quick Start
          };

          const { error: insertError } = await supabase
            .from('time_entries')
            .insert([timeEntryData])
            .select();

          if (insertError) {
            console.error('Failed to save draft:', insertError);
            return;
          }

          // Reset timer state
          set({
            isRunning: false,
            isPaused: false,
            startTime: null,
            lastPauseTime: null,
            totalBreakDuration: 0,
            projectId: null,
            activityId: null,
            description: '',
          });

        } catch (error: any) {
          console.error('Error saving draft:', error);
        }
      },

      // ================================================================
      // 🆕 NEW METHOD: Quick start project (switch)
      // ================================================================
      quickStartProject: async (projectId: string, activityId: string, description = '') => {
        const state = get();

        // If timer is running, stop and save as draft first
        if (state.isRunning) {
          await get().stopAndSaveDraft();
          // Give small delay for state update
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Start new timer
        set({
          isRunning: true,
          isPaused: false,
          startTime: Date.now(),
          lastPauseTime: null,
          totalBreakDuration: 0,
          projectId,
          activityId,
          description,
        });

        toast.success('Timer gestart!', {
          duration: 2000,
          icon: '⚡',
        });
      },
    }),
    {
      name: 'timer-storage',
    }
  )
);